/* eslint-disable no-console */
import React, { useRef, useState, useEffect } from 'react'

import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'

// const WS_URL =
//   'wss://ap-ftcloud.ifleetvision.com:22012/audio/3fd89055-5ea9-6c19-f90-1d8b59083c8e5'

interface Props {
  WS_URL: string
  handleStart: () => void
}

export default function AudioWebSocket({ WS_URL, handleStart }: Props) {
  const [isConnected, setIsConnected] = useState(false)

  const wsRef = useRef(null)
  const audioContextRef = useRef(null)
  const mediaStreamRef = useRef(null)
  const workletRef = useRef(null)

  const g711BufferRef = useRef([])
  const pktIdRef = useRef(0)
  const nextPlayTimeRef = useRef(0)
  const lastPktIdRef = useRef(null)

  // cleanup saat komponen unmount
  useEffect(() => {
    return () => {
      disconnect()
    }
  }, [])

  async function initializeAudio() {
    const mediaStream = await navigator.mediaDevices.getUserMedia({
      audio: true,
    })
    mediaStreamRef.current = mediaStream

    const audioContext = new (window.AudioContext ||
      (window as any).webkitAudioContext)()
    audioContextRef.current = audioContext

    if (audioContext.state === 'suspended') {
      await audioContext.resume()
    }
    await setupMicrophoneProcessing(audioContext, mediaStream)
    nextPlayTimeRef.current = audioContext.currentTime + 0.05
  }

  async function setupMicrophoneProcessing(audioContext, mediaStream) {
    const source = audioContext.createMediaStreamSource(mediaStream)

    await audioContext.audioWorklet.addModule(
      URL.createObjectURL(
        new Blob(
          [
            `
            class PCMProcessor extends AudioWorkletProcessor {
              process(inputs) {
                const input = inputs[0];
                if (input && input[0]) {
                  this.port.postMessage(input[0]);
                }
                return true;
              }
            }
            registerProcessor('pcm-processor', PCMProcessor);
          `,
          ],
          { type: 'application/javascript' }
        )
      )
    )

    const worklet = new AudioWorkletNode(audioContext, 'pcm-processor')
    workletRef.current = worklet
    source.connect(worklet)

    worklet.port.onmessage = (event) => {
      const float32 = event.data
      const pcm16 = floatTo16BitPCM(float32)
      const pcm8k = downsample(pcm16, audioContext.sampleRate, 8000)
      const g711a = new Uint8Array(pcm16ToG711A(pcm8k))
      g711BufferRef.current.push(...g711a)

      while (g711BufferRef.current.length >= 320) {
        const chunk = g711BufferRef.current.splice(0, 320)
        sendAudioPacket(new Uint8Array(chunk).buffer)
      }
    }
  }

  function connectWebSocket() {
    return new Promise<void>((resolve, reject) => {
      const ws = new WebSocket(WS_URL)
      wsRef.current = ws

      ws.onopen = () => {
        setIsConnected(true)
        resolve()
      }
      ws.onclose = () => {
        setIsConnected(false)
      }
      ws.onerror = () => reject(new Error('WebSocket connection failed'))
      ws.onmessage = (event) => {
        try {
          const data =
            typeof event.data === 'string'
              ? event.data
              : new TextDecoder().decode(event.data)
          const packet = JSON.parse(data)
          handleIncomingAudio(packet)
        } catch (err) {
          console.error('Message parsing error:', err)
        }
      }
    })
  }

  function sendAudioPacket(buffer) {
    const ws = wsRef.current
    if (ws && ws.readyState === WebSocket.OPEN) {
      const base64data = arrayBufferToBase64(buffer)
      const msg = {
        audioCodec: {
          audioBits: 16,
          audioChan: 1,
          audioRate: 0,
          audioSample: 8000,
          audioType: 'G711A',
          procType: 0,
        },
        data: base64data,
        pktId: pktIdRef.current++,
        pktSize: 320,
      }
      ws.send(JSON.stringify(msg))
    }
  }

  function handleIncomingAudio(packet) {
    if (!packet || !packet.data) return

    if (
      lastPktIdRef.current !== null &&
      packet.pktId !== lastPktIdRef.current + 1
    ) {
      // packet gap
    }
    lastPktIdRef.current = packet.pktId

    const rawAudio = base64ToUint8(packet.data)
    const float32Audio = alawToFloat32(rawAudio)
    scheduleAudioPlayback(float32Audio, 8000)
  }

  function scheduleAudioPlayback(float32, sampleRate) {
    const audioContext = audioContextRef.current
    if (!audioContext) return

    const buffer = audioContext.createBuffer(1, float32.length, sampleRate)
    buffer.copyToChannel(float32, 0)

    const source = audioContext.createBufferSource()
    source.buffer = buffer
    source.connect(audioContext.destination)

    const now = audioContext.currentTime
    if (nextPlayTimeRef.current < now) nextPlayTimeRef.current = now + 0.02

    source.start(nextPlayTimeRef.current)
    nextPlayTimeRef.current += float32.length / sampleRate
    source.onended = () => source.disconnect()
  }

  function disconnect() {
    if (wsRef.current) {
      wsRef.current.close()
      wsRef.current = null
    }
    if (workletRef.current) {
      workletRef.current.disconnect()
      workletRef.current = null
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((t) => t.stop())
      mediaStreamRef.current = null
    }
    if (audioContextRef.current) {
      audioContextRef.current.close()
      audioContextRef.current = null
    }
    g711BufferRef.current = []
    pktIdRef.current = 0
    nextPlayTimeRef.current = 0
    lastPktIdRef.current = null
    setIsConnected(false)
  }

  /** ==== helper functions ==== */
  function floatTo16BitPCM(float32Array) {
    const buffer = new Int16Array(float32Array.length)
    for (let i = 0; i < float32Array.length; i++) {
      let sample = Math.max(-1, Math.min(1, float32Array[i]))
      buffer[i] = sample < 0 ? sample * 0x8000 : sample * 0x7fff
    }
    return buffer
  }

  function downsample(pcm16, inRate, outRate) {
    if (inRate === outRate) return pcm16
    const ratio = inRate / outRate
    const newLength = Math.round(pcm16.length / ratio)
    const result = new Int16Array(newLength)
    let pos = 0
    for (let i = 0; i < newLength; i++) {
      result[i] = pcm16[Math.round(pos)]
      pos += ratio
    }
    return result
  }

  function pcm16ToG711A(pcm16) {
    const output = new Uint8Array(pcm16.length)
    for (let i = 0; i < pcm16.length; i++) {
      output[i] = linearToALawSample(pcm16[i])
    }
    return output.buffer
  }

  function linearToALawSample(sample) {
    let sign, exponent, mantissa
    let pcmVal = sample
    const ALAW_MAX = 0x7fff
    sign = (pcmVal & 0x8000) >> 8
    if (sign !== 0) pcmVal = -pcmVal
    if (pcmVal > ALAW_MAX) pcmVal = ALAW_MAX
    let mask
    if (pcmVal >= 256) {
      exponent = Math.floor(Math.log2(pcmVal / 256)) + 1
      mantissa = (pcmVal >> (exponent + 3)) & 0x0f
      mask = (exponent << 4) | mantissa
    } else {
      mask = pcmVal >> 4
    }
    mask ^= 0x55
    return (mask | sign) & 0xff
  }

  function arrayBufferToBase64(buffer) {
    let binary = ''
    const bytes = new Uint8Array(buffer)
    const chunkSize = 0x8000
    for (let i = 0; i < bytes.length; i += chunkSize) {
      const chunk = bytes.subarray(i, i + chunkSize)
      binary += String.fromCharCode.apply(null, chunk)
    }
    return btoa(binary)
  }

  function base64ToUint8(base64) {
    const binary = atob(base64)
    const arr = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) arr[i] = binary.charCodeAt(i)
    return arr
  }

  function alawToFloat32(uint8Array) {
    const out = new Float32Array(uint8Array.length)
    for (let i = 0; i < uint8Array.length; i++) {
      const sample = alawDecode(uint8Array[i])
      out[i] = sample / 32768
    }
    return out
  }

  function alawDecode(alaw) {
    alaw ^= 0x55
    const sign = alaw & 0x80
    const exponent = (alaw & 0x70) >> 4
    const mantissa = alaw & 0x0f
    let sample
    if (exponent === 0) {
      sample = (mantissa << 4) + 8
    } else {
      sample = ((mantissa << 4) + 0x108) << (exponent - 1)
    }
    return sign ? sample : -sample
  }

  async function handleToggle() {
    if (isConnected) {
      disconnect()
    } else {
      try {
        await initializeAudio()
        await connectWebSocket()
      } catch (err) {
        console.error('Connection failed:', err)
      }
    }
  }

  useEffect(() => {
    if (WS_URL) {
      handleToggle()
      return () => {
        disconnect()
      }
    } else {
      disconnect()
    }
  }, [WS_URL])

  return (
    <Box ml={3}>
      <Button
        size="small"
        variant="contained"
        color="secondary"
        onClick={() => handleStart()}
      >
        Start
      </Button>
    </Box>
  )
}
